import React from "react";
import { EditorState, convertToRaw } from "draft-js";
import { toJS } from "mobx";
import { Subject } from "rxjs";
import { inject, observer } from "mobx-react";
import {
  CategoryModel,
  CountryWithStateModel,
  ItemDetailInputModel,
  MediaModel,
} from "../models";
import { CategoryService } from "../services";
import { MediaStore } from "../store";
import {
  CollapsiblePanel,
  CustomLabel,
  DraftEditor,
  FlexContainer,
  Input,
  IOption,
  nameof,
  ToggleSwitch,
  withRouter,
  CreatableSelect as SelectCreateable,
  SingleSelect,
  UtilityMethods,
  AuthService,
} from "@mindmemobile/mm-shared-global";
import { ItemCategories, MediaTypes, WeightUnits } from "../enums";
import { DescriptionCell, LabelCell, ProductOptionWrapper } from "../styles";
import ProductVariantRow from "./ProductVariantRow";
import MediaBoxes from "./MediaBoxes";
import CountryDropdown from "./CountryDropdown";

type IProps = {
  itemDetail: ItemDetailInputModel;
  onUpdate: (data: ItemDetailInputModel) => void;
  isProduct: boolean;
  isDonation?: boolean;
  categoryService?: CategoryService;
  countriesWithState: CountryWithStateModel[];
  toggleLibrary: (show: boolean, fromUpload?: boolean) => void;
  showLibraryView: boolean;
};

const getWeightOptions = (): IOption[] => {
  return Object.entries(WeightUnits).map(([key, value]) => ({
    label: key,
    value: value,
  }));
};

@inject("categoryService")
class ItemDetailPage extends React.Component<IProps> {
  public categorySubject = new Subject<CategoryModel>();
  public mediaStore = new MediaStore();

  get itemDetail(): ItemDetailInputModel {
    return this.props.itemDetail;
  }

  onChange(
    value: string | number | boolean | CountryWithStateModel,
    key: keyof ItemDetailInputModel
  ) {
    const clonedObject = { ...this.itemDetail };

    switch (key) {
      case "name":
        clonedObject.name = value.toString();
        break;
      case "description":
        clonedObject.description = value.toString();
        break;
      case "isActive":
        clonedObject.isActive = value as boolean;
        break;
      case "surcharge":
        clonedObject.surcharge = value as boolean;
        break;
      case "hasVariants":
        clonedObject.hasVariants = value as boolean;
        break;
      case "discount":
        clonedObject.discount = value as boolean;
        break;
      case "review":
        clonedObject.review = value as boolean;
        break;
      case "tax":
        clonedObject.tax = value as boolean;
        break;
      case "return":
        clonedObject.return = value as boolean;
        break;
      case "sku":
        clonedObject.sku = value.toString();
        break;
      case "barCode":
        clonedObject.barCode = value.toString();
        break;
      case "returnDays":
        clonedObject.returnDays = value.toString() as string;
        break;
      case "taxId":
        const taxWithHyphen = UtilityMethods.formatTaxId(value.toString());
        clonedObject.taxId = taxWithHyphen;
        break;
      case "donationPaidTo":
        clonedObject.donationPaidTo = value.toString();
        break;
      case "taxExemptSection":
        if (typeof value === "boolean") {
          clonedObject.taxExemptSection = value ? this.c501C3 : "";
        } else {
          clonedObject.taxExemptSection = value.toString();
        }
        break;
      case "weight":
        clonedObject.weight = value.toString();
        break;
      case "weightUnit":
        clonedObject.weightUnit = value as keyof typeof WeightUnits;
        break;
      case "selectedCountry":
        const country = this.props.countriesWithState?.find(
          (country: CountryWithStateModel) => country.id === value
        );
        if (country) {
          clonedObject.selectedCountry = country;
        }
        break;
    }
    this.props.onUpdate(new ItemDetailInputModel(clonedObject));
  }

  checkBox(
    value: boolean,
    fieldName: keyof ItemDetailInputModel,
    label: string
  ) {
    return (
      <Input
        value={value}
        id={`chk${fieldName}`}
        type="checkbox"
        label={label}
        onChange={(e) => this.onChange(e.target.checked, fieldName)}
        className={"chkbox-no-margin"}
      />
    );
  }

  radioButton(
    value: boolean,
    fieldName: keyof ItemDetailInputModel,
    label: string,
    isReverse?: boolean
  ) {
    return (
      <Input
        value={value}
        id={`rdb_${fieldName}`}
        type="radio"
        label={label}
        onChange={(e) =>
          isReverse
            ? this.onChange(!e.target.checked, fieldName)
            : this.onChange(e.target.checked, fieldName)
        }
        className={"chkbox-no-margin"}
      />
    );
  }

  input(
    type: "text" | "checkbox" | "number",
    value: any,
    fieldName: keyof ItemDetailInputModel,
    placeHolder: string,
    className?: string,
    errorMessage?: string
  ) {
    return (
      <Input
        value={value}
        error={this.itemDetail.hasError(
          nameof<ItemDetailInputModel>(fieldName)
        )}
        errorMessage={
          errorMessage || this.itemDetail.getErrorMessage(fieldName)
        }
        id={`txt_${fieldName}`}
        type={type}
        placeholder={placeHolder}
        onChange={(e) => this.onChange(e.target.value, fieldName)}
        className={className}
      />
    );
  }

  get options(): any[] {
    return Object.keys(ItemCategories).map((key: string) => {
      return {
        value: key,
        label: ItemCategories[key as keyof typeof ItemCategories],
      };
    });
  }

  onChangeEditor = (editorState: EditorState) => {
    const updatedItemDetail = {
      ...this.itemDetail,
      editorState,
      description: editorState.getCurrentContent().getPlainText(),
      descriptionEditorRawContent: JSON.stringify(
        convertToRaw(editorState.getCurrentContent())
      ),
    };
    this.props.onUpdate(new ItemDetailInputModel(updatedItemDetail));
  };

  onDelete(media: MediaModel) {
    const updatedMedia = (toJS(this.itemDetail?.medias) || []).map((x) => x);
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    updatedMedia.splice(index, 1);
    this.deleteMedia(updatedMedia);
  }

  deleteMedia(medias: MediaModel[]) {
    this.props.onUpdate(
      new ItemDetailInputModel({ ...this.itemDetail, medias })
    );
  }

  updateMedia(medias: MediaModel[]) {
    this.props.onUpdate(
      new ItemDetailInputModel({
        ...this.itemDetail,
        medias: [...this.itemDetail.medias, ...medias],
      })
    );
  }

  onSelectLibraryMedia(media: MediaModel) {
    this.props.onUpdate(
      new ItemDetailInputModel({
        ...this.itemDetail,
        medias: [...this.itemDetail.medias, media],
        showLibrary: false,
      })
    );
  }

  onInputChange(value: string | number | boolean | any) {
    const updatedItemDetail = {
      ...this.itemDetail,
      hasVariants: value,
    };
    this.props.onUpdate(new ItemDetailInputModel(updatedItemDetail));
  }

  onLibraryMedia() {
    this.itemDetail.toggleShowLibrary();
    this.props.onUpdate(new ItemDetailInputModel({ ...this.itemDetail }));
  }

  get allowSection() {
    return (
      <FlexContainer className="label-description-row">
        <LabelCell>
          <CustomLabel>Allow</CustomLabel>
        </LabelCell>
        <DescriptionCell>
          <FlexContainer fxLayout="column" spacing={25} padding="8px 0px 10px">
            {!this.props.isProduct &&
              this.checkBox(this.itemDetail.tax, "tax", "Tax")}

            {/* No item level "Surcharge" toggle any more: the surcharge is
                controlled entirely by the checkout / purchase link settings. */}
            {!this.props.isDonation &&
              this.checkBox(this.itemDetail.discount, "discount", "Discount")}
            {/* {!this.props.isDonation &&
              this.checkBox(this.itemDetail.review, "review", "Reviews")} */}

            {this.props.isProduct && (
              <div>
                <FlexContainer spacing={15} alignItems="baseline">
                  <div>
                    {this.checkBox(this.itemDetail.return, "return", "Returns")}
                  </div>
                  {this.itemDetail.return && (
                    <>
                      <div className="return-days-input">
                        {this.input(
                          "number",
                          parseFloat(this.itemDetail.returnDays.toString()),
                          "returnDays",
                          ""
                        )}
                      </div>
                      <CustomLabel>days</CustomLabel>
                    </>
                  )}
                </FlexContainer>
              </div>
            )}
          </FlexContainer>
        </DescriptionCell>
      </FlexContainer>
    );
  }

  get c501C3() {
    return "501(c)3";
  }

  get taxExemptSection() {
    const c5013 = this.itemDetail.taxExemptSection === this.c501C3;
    return (
      <FlexContainer alignItems="center" spacing={30} margin="0px 0px 0px 40px">
        {this.radioButton(c5013, "taxExemptSection", "501(c)3")}
        {this.radioButton(!c5013, "taxExemptSection", "Other", true)}
        {!c5013 &&
          this.input(
            "text",
            this.itemDetail.taxExemptSection,
            "taxExemptSection",
            "",
            ""
          )}
      </FlexContainer>
    );
  }

  get donationAdvanceOptions() {
    if (!this.props.isDonation) return null;
    return (
      <>
        <FlexContainer className="label-description-row" alignItems="center">
          <LabelCell>
            <CustomLabel>Donation Paid To</CustomLabel>
          </LabelCell>
          <DescriptionCell>
            {this.input(
              "text",
              this.itemDetail.donationPaidTo,
              "donationPaidTo",
              "Donation Received By",
              "styled-input barcode-input"
            )}
          </DescriptionCell>
        </FlexContainer>
        <FlexContainer className="label-description-row" alignItems="center">
          <LabelCell>
            <CustomLabel>Tax Deductible</CustomLabel>
          </LabelCell>
          <DescriptionCell>
            <FlexContainer spacing={70} alignItems="center">
              <div>
                <FlexContainer spacing={30} alignItems="center">
                  {this.radioButton(!this.itemDetail.tax, "tax", "No", true)}
                  {this.radioButton(this.itemDetail.tax, "tax", "Yes")}

                  <div>{this.itemDetail.tax && this.taxExemptSection}</div>
                </FlexContainer>
              </div>
            </FlexContainer>
          </DescriptionCell>
        </FlexContainer>
        {this.itemDetail.tax && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel>Tax ID</CustomLabel>
            </LabelCell>
            <DescriptionCell>
              {this.input(
                "text",
                this.itemDetail.taxId,
                "taxId",
                "Tax ID",
                "styled-input barcode-input",
                "Please enter 9 digit Tax ID"
              )}
            </DescriptionCell>
          </FlexContainer>
        )}
      </>
    );
  }

  get advancedSection(): React.ReactNode {

    const selectedCountry = this.props.countriesWithState?.find(
      (country) =>
        country.id === this.itemDetail.selectedCountry?.id
    ) || this.itemDetail.selectedCountry; 
    return (
      <CollapsiblePanel title="Advanced" className="advanced-row-margin">
        {this.props.isProduct && (
          <FlexContainer className="label-description-row">
            <ProductVariantRow
              key={"field.dataKey"}
              checked={Boolean(this.itemDetail.hasVariants)}
              onChange={(updatedValue) => this.onInputChange(updatedValue)}
              showExample={this.props.itemDetail.showExample}
              toggleShowExample={() => {
                this.props.itemDetail.toggleShowExample();
                this.props.onUpdate(
                  new ItemDetailInputModel({ ...this.itemDetail })
                );
              }}
            />
          </FlexContainer>
        )}
        {this.allowSection}
        {/* {this.donationAdvanceOptions} */}
        {!this.props.isDonation && !this.itemDetail.hasVariants && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel>SKU</CustomLabel>
            </LabelCell>
            <DescriptionCell>
              {this.input(
                "text",
                this.itemDetail.sku,
                "sku",
                "SKU",
                "styled-input barcode-input"
              )}
            </DescriptionCell>
          </FlexContainer>
        )}

        {!this.props.isDonation && !this.itemDetail.hasVariants && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel>Bar Code</CustomLabel>
            </LabelCell>
            <DescriptionCell>
              {this.input(
                "text",
                this.itemDetail.barCode,
                "barCode",
                "Barcode (UPC, ISBN...)",
                "styled-input barcode-input"
              )}
            </DescriptionCell>
          </FlexContainer>
        )}
        {this.props.isProduct && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel>Weight</CustomLabel>
            </LabelCell>
            <DescriptionCell>
              <FlexContainer alignItems="center" spacing={10}>
                {this.input(
                  "text",
                  this.itemDetail.weight,
                  "weight",
                  "0.00",
                  "styled-input weight-input"
                )}
                <SingleSelect
                  selectedKey={this.itemDetail.weightUnit}
                  nameKeys={["label"]}
                  valueKeys={["value"]}
                  options={[getWeightOptions()]}
                  onChange={(option) =>
                    this.onChange(option.value, "weightUnit")
                  }
                  className="dpdn-no-separator weight-dropdown"
                />
              </FlexContainer>
            </DescriptionCell>
          </FlexContainer>
        )}
        {this.props.isProduct && (
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel>Country of Origin</CustomLabel>
            </LabelCell>
            <DescriptionCell className="country-origin-dpdn">
              <CountryDropdown
                className="country-dropdown country-origin-upward"
                selectedCountry={selectedCountry}
                error={this.itemDetail.hasError(
                  nameof<ItemDetailInputModel>("selectedCountry")
                )}
                errorMessage={"Please select country"}
                onCountryChange={(id: string) =>
                  this.onChange(id, "selectedCountry")
                }
                availableCountries={this.props.countriesWithState || []}
              />
            </DescriptionCell>
          </FlexContainer>
        )}
      </CollapsiblePanel>
    );
  }

  get categoryOptions(): IOption[] {
    return (
      this.props.categoryService?.categories?.map((o) => {
        return { label: o.name, value: o.id };
      }) || []
    );
  }

  get selectedCategories(): IOption[] {
    if (!this.itemDetail.category) return [];
    return [
      {
        label: this.itemDetail.category.name,
        value: this.itemDetail.category.id,
      },
    ];
  }

  onCreateOrSelectCategory(newValue: IOption) {
    let hasNew = false;
    if (newValue) {
      if (newValue.__isNew__) {
        hasNew = true;
        this.props.categoryService?.createCategory(
          AuthService.authData.accountId,
          newValue.label,

          this.categorySubject
        );

        this.categorySubject.subscribe({
          next: (newCategory) => {
            if (newCategory) {
              this.props.onUpdate(
                new ItemDetailInputModel({
                  ...this.itemDetail,
                  category: newCategory,
                })
              );
            }
          },
        });
      }
    }
    if (!hasNew) {
      const matchedCategory =
        newValue &&
        this.props.categoryService?.categories.find(
          (c) => c.id === newValue.value
        );
      this.props.onUpdate(
        new ItemDetailInputModel({
          ...this.itemDetail,
          category: matchedCategory || null,
        })
      );
    }
  }

  onClose() {
    this.itemDetail.toggleShowLibrary();
    this.props.onUpdate(new ItemDetailInputModel({ ...this.itemDetail }));
  }

  onFavourite(media: MediaModel) {
    const updatedMedia = (toJS(this.itemDetail?.medias) || []).map((x) => {
      if (x.mediaId === media.mediaId) {
        return { ...x, isFavourite: true } as MediaModel;
      } else if (x.isFavourite) {
        return { ...x, isFavourite: false } as MediaModel;
      }
      return x;
    });
    const index = updatedMedia.findIndex((x) => x.mediaId === media.mediaId);
    if (index !== -1) {
      const [favouritedMedia] = updatedMedia.splice(index, 1);
      updatedMedia.unshift(favouritedMedia);
    }
    this.props.onUpdate(
      new ItemDetailInputModel({
        ...this.itemDetail,
        medias: [...updatedMedia],
      })
    );
  }

  render(): React.ReactNode {
    return (
      <>
        <ProductOptionWrapper>
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel>Name</CustomLabel>
            </LabelCell>
            <div>
              <FlexContainer spacing={15} alignItems="center">
                {this.input(
                  "text",
                  this.itemDetail.name,
                  "name",
                  "Name",
                  "styled-input prod-name-input"
                )}
                <CustomLabel
                  lineHeight="40px"
                  fontSize="16px"
                  margin="0px 0px 0px 50px"
                >
                  Status
                </CustomLabel>
                <DescriptionCell>
                  <ToggleSwitch
                    on={this.itemDetail.isActive}
                    onChange={(value) => this.onChange(value, "isActive")}
                  />
                </DescriptionCell>
              </FlexContainer>
            </div>
          </FlexContainer>
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel>Description</CustomLabel>
            </LabelCell>
            <DescriptionCell>
              <DraftEditor
                editorState={this.itemDetail.editorState}
                onChange={(newValue: EditorState) =>
                  this.onChangeEditor?.(newValue)
                }
                className="product-detail-editor"
              />
            </DescriptionCell>
          </FlexContainer>
          <FlexContainer className="label-description-row">
            <LabelCell>
              <CustomLabel>Category</CustomLabel>
            </LabelCell>
            <DescriptionCell>
              <SelectCreateable
                isClearable={true}
                isMulti={false}
                options={this.categoryOptions}
                selectedOptions={this.selectedCategories}
                onSingleChange={(newValue) =>
                  this.onCreateOrSelectCategory(newValue)
                }
                className="option-category-select"
                classPrefix="option-select"
              />
            </DescriptionCell>
          </FlexContainer>
          <MediaBoxes
            key={"medias"}
            multiple={true}
            medias={toJS(this.itemDetail.medias) || []}
            onUpload={(medias: MediaModel[]) => this.updateMedia(medias)}
            onDelete={(media: MediaModel) => this.onDelete(media)}
            onSelectLibraryMedia={(media) =>
              this.onSelectLibraryMedia(MediaModel.mediaItemToMediaModel(media))
            }
            onFavourite={(media: MediaModel) => this.onFavourite(media)}
            allowedMediaTypes={[MediaTypes.Image, MediaTypes.Video]}
            toggleLibrary={(show, fromUpload) =>
              this.props.toggleLibrary(show, fromUpload)
            }
            showLibrary={this.props.showLibraryView}
          />
          {this.advancedSection}
        </ProductOptionWrapper>
      </>
    );
  }
}

export default withRouter(observer(ItemDetailPage));
